<template>
  <div class="space-y-6">
    <div>
      <h2 class="text-base font-semibold text-gray-900">Test Report Settings</h2>
      <p class="text-sm text-gray-500 mt-0.5">Configure how the AI identifies job numbers in uploaded test report PDFs</p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="bg-white rounded-xl border border-gray-200 py-16 text-center">
      <i class="fa-solid fa-spinner fa-spin text-gray-400 text-xl mb-3"></i>
      <p class="text-sm text-gray-400">Loading settings...</p>
    </div>

    <template v-else>
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <label class="block text-sm font-medium text-gray-900 mb-1">AI Brief</label>
        <p class="text-xs text-gray-400 mb-3">Tell the AI where to find the Job Number in uploaded PDF files. For example: "Look for 'Project #' followed by 4 digits" or "The job number appears in the header as #0001".</p>
        <textarea
          v-model="form.aiBrief"
          rows="6"
          class="w-full text-sm border border-gray-200 rounded-lg p-3 outline-none focus:border-indigo-400 bg-gray-50 resize-y"
          placeholder="e.g. Look for 'Project #' followed by 4 digits in the first page header..."
        ></textarea>
      </div>

      <!-- Save -->
      <div class="flex justify-end">
        <button @click="save()" :disabled="saving"
          class="inline-flex items-center px-5 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-colors disabled:opacity-50"
          style="height:40px">
          <i v-if="saving" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>

      <div v-if="saved" class="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
        <i class="fa-solid fa-circle-check text-green-600"></i>
        <span class="text-sm text-green-700 font-medium">Test report settings saved</span>
      </div>
    </template>
  </div>
</template>

<script>
import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";

const API_BASE = "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/settings";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      saved: false,
      form: {
        aiBrief: "",
      },
    };
  },
  methods: {
    getCurrentUser() {
      return new Promise((resolve, reject) => {
        if (auth.currentUser) return resolve(auth.currentUser);
        const unsub = onAuthStateChanged(auth, (user) => {
          unsub();
          user ? resolve(user) : reject(new Error('Not authenticated'));
        });
      });
    },
    async getHeaders() {
      const user = await this.getCurrentUser();
      const token = await user.getIdToken();
      return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    },
    async fetchData() {
      this.loading = true;
      try {
        const headers = await this.getHeaders();
        const res = await fetch(`${API_BASE}/tests`, { headers });
        const json = await res.json();
        const data = json.data || {};
        if (data.aiBrief !== undefined) this.form.aiBrief = data.aiBrief;
      } catch (e) {
        console.error('Failed to load test settings:', e);
      }
      this.loading = false;
    },
    async save() {
      this.saving = true;
      this.saved = false;
      try {
        const headers = await this.getHeaders();
        await fetch(`${API_BASE}/tests`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(this.form),
        });
        this.saved = true;
        setTimeout(() => { this.saved = false; }, 3000);
      } catch (e) {
        console.error('Failed to save test settings:', e);
      }
      this.saving = false;
    },
  },
  created() {
    this.fetchData();
  },
};
</script>
